/* eslint-disable no-unused-vars */
import moment from "moment";
import Teachr from "../../Objects/models/Teachr";
import TeachrContact from "../../Objects/models/TeachrContact";
import ClientRepository from "../../Repositories/ClientRepository";
import TeachrContactRepository from "../../Repositories/TeachrContactRepository";
import URSSAFParentRepository from "../../Repositories/URSSAFParentRepository";
import ApiService from "../../Services/ApiService";
import Functions from "../../Helpers/Functions";
import {
  auth,
  registerWithEmailAndPassword,
  logInWithEmailAndPassword,
  FirebaseAuthServiceErrorCode,
} from "../../Services/FirebaseService";
import { ENV_DECLARONS, isBrandActive } from "../../config/environment.config";
import {
  AddressData,
  BirthBankData,
  ConditionsData,
  PersonalInfoData,
} from "../../Hooks/InterfaceAiciOnboarding";

export interface RunAiciSubmissionParams {
  teachrContact: TeachrContact;
  teachr?: Teachr;
  personal: PersonalInfoData;
  birthBank: BirthBankData;
  address: AddressData;
  conditions: ConditionsData;
  isBornFrance: boolean;
}

/** Met une chaîne en MAJUSCULES sans accents (attendu par l'URSSAF). */
const removeAccents = (str?: string): string => {
  if (!str) return "";
  const upper = str.normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase();
  return upper.includes("Œ") ? upper.replaceAll("Œ", "OE") : upper;
};

/** Erreur "métier" portant un message déjà destiné à l'utilisateur. */
export class AiciSubmissionError extends Error {
  /**
   * Détail des erreurs de validation renvoyées par l'API (errors[].message),
   * à afficher sous forme de liste. Vide si l'API n'a pas fourni de détail.
   */
  details: string[];

  constructor(message: string, details: string[] = []) {
    super(message);
    this.details = details;
  }
}

/**
 * Soumission finale du parcours AICI.
 *
 * 1. Authentification Firebase (register, ou login si l'e-mail existe déjà).
 * 2. UN SEUL appel à l'endpoint consolidé backend : client → adresse → enfant → favori
 *    → customFees (transactionnel + reprenable côté serveur via TeachrContact.aiciProcess).
 * 3. Inscription URSSAF (tpres) appel séparé.
 * 4. Finalisation du TeachrContact : step = 5 (seulement si tpres a réussi).
 *
 * Lève AiciSubmissionError (message destiné à l'utilisateur) en cas d'échec.
 */
export async function runAiciSubmission(
  params: RunAiciSubmissionParams,
): Promise<void> {
  const { teachrContact, teachr } = params;

  if (teachrContact.isDelete) {
    throw new AiciSubmissionError(
      "Vous ne pouvez pas continuer cette inscription car le contact a été supprimé. Veuillez contacter votre professeur.",
    );
  }
  // Déjà finalisé : rien à refaire (idempotence).
  if (Number(teachrContact.step) === 5) return;

  // ── 1. Authentification Firebase ────────────────────────────────────────
  await ensureFirebaseAuth(teachrContact.mail, teachrContact.key);
  if (!auth.currentUser) {
    throw new AiciSubmissionError(
      "Impossible de vous authentifier. Veuillez réessayer.",
    );
  }
  const token: string = await auth.currentUser.getIdToken();
  ApiService.disableAuth();

  // ── 2. Endpoint consolidé (client + adresse + enfant + favori + customFees) ─
  const payload = buildOnboardingPayload(params, token);
  let result: any;
  try {
    ApiService.disableAuth();
    result = await ClientRepository.aiciOnboarding(payload);
  } catch (error: any) {
    throw buildSubmissionError(error);
  }
  if (result && result.success === false) {
    throw buildSubmissionError(result);
  }

  // ── 3. Inscription URSSAF (tpres) séparée ─────────────────────────────
  ApiService.enableAuth(undefined, undefined, "client " + token);

  const tpresPayload = buildTpresPayload(params);
  try {
    await URSSAFParentRepository.tpresInscription(tpresPayload);
  } catch (error: any) {
    ApiService.disableAuth();
    throw buildSubmissionError(error);
  }

  // ── 4. Finalisation du TeachrContact (step = 5) ─────────────────────────
  // L'adresse a été créée/liée par l'endpoint consolidé et aiciProcess est géré côté
  // serveur : on ne les renvoie pas (éviter de les écraser). On ne pose que le step final.
  if (teachr && teachr["@id"]) {
    teachrContact.teachr = teachr["@id"];
  }
  delete (teachrContact as any).address;
  delete (teachrContact as any).aiciProcess;
  teachrContact.step = 5;
  await TeachrContactRepository.updateTeachrContact(teachrContact);
}

export interface RunAiciSelfSubmissionParams {
  /** Client connecté (espace client), source des champs de compte. */
  client: any;
  /** Clé du TeachrContact quand l'activation vient d'une invitation du prof. */
  contactKey?: string;
  /** Code d'invitation du prof (activation autonome), optionnel. */
  invitationCode?: string;
  personal: PersonalInfoData;
  birthBank: BirthBankData;
  address: AddressData;
  conditions: ConditionsData;
  isBornFrance: boolean;
}

/**
 * Activation AICI depuis l'espace client (client déjà authentifié) :
 * 1. Endpoint consolidé en mode "self" : mise à jour du client, adresse,
 *    élève ; favori + customFees uniquement si un prof est fourni (clé de
 *    contact ou code d'invitation).
 * 2. Inscription URSSAF (tpres), qui pose idClientTiersPrestation.
 * 3. Si une clé de contact est fournie, finalisation de la mise en relation
 *    (step 5), sans bloquer l'activation en cas d'échec.
 */
export async function runAiciSelfSubmission(
  params: RunAiciSelfSubmissionParams,
): Promise<void> {
  const { client, contactKey, invitationCode } = params;

  if (!auth.currentUser) {
    throw new AiciSubmissionError(
      "Votre session a expiré. Merci de vous reconnecter, puis de réessayer.",
    );
  }
  const token: string = await auth.currentUser.getIdToken();

  // ── 1. Endpoint consolidé, mode self ────────────────────────────────────
  const payload = buildSelfOnboardingPayload(params, token);
  let result: any;
  try {
    result = await ClientRepository.aiciOnboarding(payload);
  } catch (error: any) {
    throw buildSubmissionError(error);
  }
  if (result && result.success === false) {
    throw buildSubmissionError(result);
  }

  // ── 2. Inscription URSSAF (tpres) ───────────────────────────────────────
  ApiService.enableAuth(undefined, undefined, "client " + token);
  const tpresPayload = buildTpresPayload({
    teachrContact: undefined as any,
    teachr: undefined,
    personal: params.personal,
    birthBank: params.birthBank,
    address: params.address,
    conditions: params.conditions,
    isBornFrance: params.isBornFrance,
  });
  try {
    await URSSAFParentRepository.tpresInscription(tpresPayload);
  } catch (error: any) {
    throw buildSubmissionError(error);
  }

  // ── 3. Finalisation de la mise en relation (step 5), best effort ─────────
  if (contactKey) {
    try {
      await ClientRepository.aiciLink({ teachrContactKey: contactKey });
    } catch (error) {
      console.warn("AICI: échec de finalisation de la mise en relation", error);
    }
  } else if (invitationCode) {
    // le favori et les customFees ont déjà été créés par le consolidé ; rien
    // d'autre à finaliser sans contact
  }
}

/**
 * Payload du consolidé en mode self : le client existe déjà, on n'écrase pas
 * les champs de compte (mail = celui du compte connecté, jamais la saisie).
 * Le prof est résolu côté backend (clé de contact prioritaire, sinon code
 * d'invitation) ; customFees est construit côté backend dans ce mode.
 */
function buildSelfOnboardingPayload(
  params: RunAiciSelfSubmissionParams,
  token: string,
): Record<string, any> {
  const { client, contactKey, invitationCode, personal, birthBank, address, conditions } = params;
  const sex = personal.gender === "1" ? "MAN" : "WOMAN";
  const phone = Functions.formatPhoneNumber(personal.phoneNumber);

  const clientPayload: Record<string, any> = {
    firebaseAuthToken: token,
    firstName: personal.firstName,
    lastName: personal.name,
    phone,
    mail: client?.mail ?? personal.mail,
    sex,
    isNotify: conditions.isNotify,
  };
  if (isBrandActive(ENV_DECLARONS)) {
    clientPayload.brand = ENV_DECLARONS;
  }

  const streetName =
    `${address.streetLetter ? " " + address.streetLetter.name : ""} ${address.streetType?.name ?? ""} ${address.streetLabel}`
      .replace(/\s+/g, " ")
      .trim();
  const formattedAddress =
    `${address.streetNumber}${address.streetLetter ? " " + address.streetLetter.name : ""} ${address.streetType?.name ?? ""} ${address.streetLabel}, ${address.zipCode} ${address.city}`
      .replace(/\s+/g, " ")
      .trim();

  const addressPayload: Record<string, any> = {
    googlePlaceId: "googlePlaceId",
    streetNumber: address.streetNumber,
    streetName,
    city: address.city,
    department: (address.zipCode || "").substring(0, 2),
    region: "",
    country: "France",
    zip: address.zipCode,
    latitude: "",
    longitude: "0",
    url: "",
    formattedAddress,
    owner: "",
    isDefault: true,
    houseCode: "",
    intercom: "",
    extraInformation: address.complement || "",
    streetNameShort: address.streetLabel,
    sessionToken: "",
  };

  const childBirthday = moment(birthBank.birthDay, "DD-MM-YYYY", true).isValid()
    ? moment(birthBank.birthDay, "DD-MM-YYYY", true).format("YYYY-MM-DD")
    : birthBank.birthDay;
  const childPayload: Record<string, any> = {
    firstName: (personal.studentName || personal.firstName).trim(),
    lastName: personal.name,
    parent: "",
    class: 0,
    birthday: childBirthday,
    phone,
    otherInformation: "",
  };

  const favoriteTeachr = !contactKey && invitationCode
    ? { invitationCode: invitationCode.trim() }
    : null;

  return {
    token,
    teachrContactKey: contactKey ?? null,
    client: clientPayload,
    address: addressPayload,
    child: childPayload,
    favoriteTeachr,
    customFees: null,
  };
}

/** Authentifie le client : tente l'inscription, bascule en connexion si l'e-mail existe déjà. */
async function ensureFirebaseAuth(mail: string, key?: string): Promise<void> {
  // Reprise : on ne réutilise la session que si elle correspond bien au même e-mail
  // (sinon on se ré-authentifie pour ne pas poursuivre sous un autre compte).
  if (
    auth.currentUser &&
    auth.currentUser.email?.toLowerCase() === mail?.toLowerCase()
  ) {
    return;
  }
  if (!key) {
    throw new AiciSubmissionError(
      "Lien d'inscription invalide (clé manquante). Veuillez contacter votre professeur.",
    );
  }
  try {
    await registerWithEmailAndPassword(mail, key);
  } catch (error: any) {
    if (error?.code === FirebaseAuthServiceErrorCode.EMAIL_ALREADY_UN_USE) {
      const response = await logInWithEmailAndPassword(mail, key);
      console.log(response, "response");
    } else {
      throw error;
    }
  }
}

/**
 * Construit le payload de l'endpoint consolidé : les objets (client, adresse, enfant,
 * customFees) tels que construits aujourd'hui. Les références au @id du client
 * (owner / customFees.client / child.parent) partent vides : le backend les remplit.
 */
function buildOnboardingPayload(
  params: RunAiciSubmissionParams,
  token: string,
): Record<string, any> {
  const { teachrContact, teachr, personal, birthBank, address, conditions } =
    params;
  const sex = personal.gender === "1" ? "MAN" : "WOMAN";
  const phone = Functions.formatPhoneNumber(personal.phoneNumber);

  const client: Record<string, any> = {
    firebaseAuthToken: token,
    firstName: personal.firstName,
    lastName: personal.name,
    phone,
    acceptMarketing: conditions.acceptMarketingEmail,
    firebaseMessagingToken: "firebaseMessagingToken",
    isParent: true,
    isPremium: false,
    isRegisteredByTeachr: true,
    mail: personal.mail,
    sex,
    isNotify: conditions.isNotify,
  };
  if (isBrandActive(ENV_DECLARONS)) {
    client.brand = ENV_DECLARONS;
  }

  // streetName = lettre + type + libellé (combinaison demandée).
  const streetName =
    `${address.streetLetter ? " " + address.streetLetter.name : ""} ${address.streetType?.name ?? ""} ${address.streetLabel}`
      .replace(/\s+/g, " ")
      .trim();
  const formattedAddress =
    `${address.streetNumber}${address.streetLetter ? " " + address.streetLetter.name : ""} ${address.streetType?.name ?? ""} ${address.streetLabel}, ${address.zipCode} ${address.city}`
      .replace(/\s+/g, " ")
      .trim();

  const addressPayload: Record<string, any> = {
    googlePlaceId: "googlePlaceId",
    streetNumber: address.streetNumber,
    streetName,
    city: address.city,
    department: (address.zipCode || "").substring(0, 2),
    region: "",
    country: "France",
    zip: address.zipCode,
    latitude: "",
    longitude: "0",
    url: "",
    formattedAddress,
    owner: "", // rempli backend (@id du client créé)
    isDefault: true,
    houseCode: "",
    intercom: "",
    extraInformation: address.complement || "",
    streetNameShort: address.streetLabel,
    sessionToken: "",
  };

  const childBirthday = moment(birthBank.birthDay, "DD-MM-YYYY", true).isValid()
    ? moment(birthBank.birthDay, "DD-MM-YYYY", true).format("YYYY-MM-DD")
    : birthBank.birthDay;
  const childPayload: Record<string, any> = {
    firstName: (personal.studentName || personal.firstName).trim(),
    lastName: personal.name,
    parent: "", // rempli backend (@id du client créé)
    class: 0,
    birthday: childBirthday,
    phone,
    otherInformation: "",
  };

  const favoriteTeachr = teachr?.id ? { teachrId: teachr.id } : null;
  const customFees =
    teachr && teachr["@id"]
      ? {
          client: [""], // rempli backend (@id du client créé)
          teachr: [teachr["@id"]],
          pricePerHour: null,
          feesPerHour: null,
          isTeachrContact: true,
        }
      : null;

  return {
    token,
    teachrContactKey: teachrContact.key,
    client,
    address: addressPayload,
    child: childPayload,
    favoriteTeachr,
    customFees,
  };
}

/** Construit le payload d'inscription URSSAF (tpres) à partir des données du formulaire. */
function buildTpresPayload(
  params: RunAiciSubmissionParams,
): Record<string, any> {
  const { teachr, personal, birthBank, address, isBornFrance } = params;
  // Nom de naissance demandé uniquement aux femmes : pour un homme on transmet le nom d'usage.
  const effectiveBirthName = (
    personal.gender === "2"
      ? personal.birthName || personal.name
      : personal.name
  ).trim();
  const birthDate = moment(birthBank.birthDay, "DD-MM-YYYY", true).isValid()
    ? moment(birthBank.birthDay, "DD-MM-YYYY", true).format("YYYY-MM-DD")
    : moment(birthBank.birthDay).format("YYYY-MM-DD");

  return {
    gender: personal.gender,
    birth_name: effectiveBirthName,
    birthDate,
    country_birth_code: isBornFrance
      ? "99100"
      : `99${Functions.getCodePays(birthBank.country)}`,
    birth_zip_code: isBornFrance ? birthBank.birthZipCode : undefined,
    birth_wordingCommune: isBornFrance
      ? (birthBank.birthCity || "").toUpperCase()
      : undefined,
    way_type_code: address.streetType?.code ?? "",
    country_code: "99100",
    city: removeAccents(address.city),
    streetway_name: address.streetLabel,
    streetway_number: address.streetNumber,
    zip_code: address.zipCode,
    bic: birthBank.bic,
    iban: birthBank.iban,
    account_holder: birthBank.accountHolder,
    named_place: address.lieuDit || "",
    streetway_letter: address.streetLetter?.code ?? null,
    scoop: teachr?.scoop,
  };
}

/**
 * Extrait un message utilisateur exploitable d'une erreur API (même forme que tpres)
 * ainsi que le détail des erreurs de validation (errors[]) le cas échéant.
 */
function parseApiError(error: any): { message: string; details: string[] } {
  let parsed: any = error;
  
  if (typeof error?.message === "string") {
    try {
      parsed = JSON.parse(error.message);
    } catch {
      /* message non-JSON : on garde l'objet tel quel */
    }
  }

  // Détail des erreurs de validation : on liste chaque message (fallback sur le champ).
  const details: string[] = Array.isArray(parsed?.errors)
    ? parsed.errors
        .map((e: any) => e?.message || e?.field)
        .filter((m: any): m is string => typeof m === "string" && m.length > 0)
    : [];

  let message: string;
  if (parsed?.code === "ERR_CONTACT_ADRESSE_TYPEVOIE") {
    message = parsed.message;
  } else if (parsed?.translate) {
    message = parsed.translate;
  } else if (parsed?.message) {
    message = parsed.message;
  } else {
    message =
      error?.message ||
      "Une erreur est survenue lors de la création de votre compte. Veuillez réessayer.";
  }

  return { message, details };
}

/** Construit une AiciSubmissionError (message + liste des erreurs) depuis une erreur API. */
function buildSubmissionError(error: any): AiciSubmissionError {
  const { message, details } = parseApiError(error);
  return new AiciSubmissionError(message, details);
}
